import React, { useEffect, useRef } from 'react';
import { Animated, View, StyleSheet } from 'react-native';
import { useTheme } from '../../../theme';

interface TableSkeletonProps {
  rows?: number;
  /** Optional override; when provided, overrides the theme-derived isDark. */
  isDark?: boolean;
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 8, isDark: isDarkProp }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = isDarkProp !== undefined ? isDarkProp : resolvedTheme === 'dark';
  const shimmer = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmer, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(shimmer, { toValue: 0, duration: 900, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer]);

  const opacity = shimmer.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.7] });

  // Simpler column layout: a few flexible skeleton blocks matching table columns
  const renderRow = (key: number) => (
    <Animated.View key={key} style={[styles.row, { opacity }]}>
      <View style={[styles.cell, { flex: 1.6 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 1.6 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 2 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
      <View style={[styles.cell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
    </Animated.View>
  );

  return (
    <View style={[styles.container, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1 }]}>
      <Animated.View style={[styles.header, { opacity }]}> 
        <View style={[styles.headerCell, { flex: 1.6 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 1.6 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 2 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
        <View style={[styles.headerCell, { flex: 1 }, isDark && { backgroundColor: colors.background.elevated }]} />
      </Animated.View>

      <View>
        {Array.from({ length: rows }).map((_, i) => renderRow(i))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  headerCell: {
    height: 12,
    borderRadius: 6,
    backgroundColor: '#E6EEF7',
    marginRight: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F4F8',
  },
  cell: {
    height: 14,
    borderRadius: 6,
    backgroundColor: '#F1F5F9',
    marginRight: 12,
  },
});

export default TableSkeleton;

